import * as React from "react";
import {
    Box,
    Button,
    ExpandableSection,
    SpaceBetween,
} from "@amzn/awsui-components-react";

export interface PathStep {
    /** The overview/question text of the node */
    questionText: string;
    /** The answer the user selected (undefined for the final leaf node) */
    selectedAnswer?: string;
    /** The node ID for reference */
    nodeId: string;
}

export const PathSummary = (props: {
    pathSteps: PathStep[];
}) => {
    const [copySuccess, setCopySuccess] = React.useState(false);

    if (!props.pathSteps || props.pathSteps.length === 0) {
        return null;
    }

    const formatSummaryAsText = (): string => {
        const lines: string[] = [];
        lines.push("Decision Tree Path Summary");
        lines.push("=".repeat(40));
        lines.push("");

        props.pathSteps.forEach((step, index) => {
            const stepNumber = index + 1;
            const isLast = index === props.pathSteps.length - 1;

            if (isLast && !step.selectedAnswer) {
                // Final/leaf node - show as outcome
                lines.push(`Outcome:`);
                lines.push(`  ${step.questionText}`);
            } else {
                lines.push(`Step ${stepNumber}:`);
                lines.push(`  Question: ${step.questionText}`);
                if (step.selectedAnswer) {
                    lines.push(`  Answer: ${step.selectedAnswer}`);
                }
            }
            lines.push("");
        });

        lines.push("=".repeat(40));
        lines.push(`Generated on: ${new Date().toLocaleString()}`);

        return lines.join("\n");
    };

    const handleCopyToClipboard = async () => {
        try {
            const text = formatSummaryAsText();
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement("textarea");
            textArea.value = formatSummaryAsText();
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand("copy");
            document.body.removeChild(textArea);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        }
    };

    const handleDownloadAsText = () => {
        const text = formatSummaryAsText();
        const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `decision-tree-summary-${new Date().toISOString().slice(0, 10)}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownloadAsCSV = () => {
        const rows: string[][] = [["Step", "Question", "Answer"]];
        props.pathSteps.forEach((step, index) => {
            const isLast = index === props.pathSteps.length - 1;
            const stepLabel = isLast && !step.selectedAnswer ? "Outcome" : `Step ${index + 1}`;
            const escapeCsv = (val: string) => `"${(val || "").replace(/"/g, "\"\"")}"`;
            rows.push([
                escapeCsv(stepLabel),
                escapeCsv(step.questionText),
                escapeCsv(step.selectedAnswer || "—"),
            ]);
        });
        const csvContent = rows.map(r => r.join(",")).join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `decision-tree-summary-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <ExpandableSection
            variant="container"
            headerText="Path Summary"
            headerDescription="Click to view or export the path you took through this decision tree"
            defaultExpanded={false}
        >
            <SpaceBetween size="m" direction="vertical">
                {/* Export actions */}
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button
                            iconName="copy"
                            onClick={handleCopyToClipboard}
                            variant="normal"
                        >
                            {copySuccess ? "Copied!" : "Copy"}
                        </Button>
                        <Button
                            iconName="download"
                            onClick={handleDownloadAsText}
                            variant="normal"
                        >
                            Download TXT
                        </Button>
                        <Button
                            iconName="download"
                            onClick={handleDownloadAsCSV}
                            variant="normal"
                        >
                            Download CSV
                        </Button>
                    </SpaceBetween>
                </Box>

                {/* Timeline */}
                <div style={{ padding: "8px 0" }}>
                    {props.pathSteps.map((step, index) => {
                        const isLast = index === props.pathSteps.length - 1;
                        const isOutcome = isLast && !step.selectedAnswer;

                        return (
                            <div
                                key={`${step.nodeId}-${index}`}
                                style={{
                                    display: "flex",
                                    position: "relative",
                                    paddingBottom: isLast ? "0" : "16px",
                                }}
                            >
                                {/* Timeline line + dot */}
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        marginRight: "16px",
                                        minWidth: "24px",
                                    }}
                                >
                                    <div
                                        style={{
                                            width: "12px",
                                            height: "12px",
                                            borderRadius: "50%",
                                            backgroundColor: isOutcome ? "#037f0c" : "#0972d3",
                                            border: `2px solid ${isOutcome ? "#037f0c" : "#0972d3"}`,
                                            flexShrink: 0,
                                            marginTop: "4px",
                                        }}
                                    />
                                    {!isLast && (
                                        <div
                                            style={{
                                                width: "2px",
                                                flexGrow: 1,
                                                backgroundColor: "#d1d5db",
                                                marginTop: "4px",
                                            }}
                                        />
                                    )}
                                </div>

                                {/* Content */}
                                <div style={{ flex: 1, paddingBottom: "4px" }}>
                                    {isOutcome ? (
                                        <div>
                                            <Box variant="awsui-key-label" color="text-status-success">
                                                <strong>Outcome</strong>
                                            </Box>
                                            <Box variant="p" margin={{ top: "xxs" }}>
                                                {step.questionText}
                                            </Box>
                                        </div>
                                    ) : (
                                        <div>
                                            <Box variant="awsui-key-label">
                                                Step {index + 1}
                                            </Box>
                                            <Box variant="p" margin={{ top: "xxs" }}>
                                                <strong>Q:</strong> {step.questionText}
                                            </Box>
                                            {step.selectedAnswer && (
                                                <Box
                                                    variant="p"
                                                    margin={{ top: "xxs" }}
                                                    color="text-status-info"
                                                >
                                                    <strong>A:</strong> {step.selectedAnswer}
                                                </Box>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </SpaceBetween>
        </ExpandableSection>
    );
};
